/**
 * Transaction process graph for product orders:
 *   - default-purchase
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer makes an order for a listing, a transaction is
  // created with the initial request-payment transition.
  // At this transition a PaymentIntent is created by Marketplace API.
  // After this transition, the actual payment must be made on client-side directly to Stripe.
  REQUEST_PAYMENT: 'transition/request-payment',

  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a request.
  INQUIRE: 'transition/inquire',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.
  CONFIRM_PAYMENT: 'transition/confirm-payment',

  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // Provider or opeartor can mark the product shipped/delivered
  MARK_DELIVERED: 'transition/mark-delivered',
  OPERATOR_MARK_DELIVERED: 'transition/operator-mark-delivered',

  // CUSTOM-START: shipment-tracking
  // Seller adds or edits tracking details; the order stays in delivered.
  UPDATE_TRACKING: 'transition/update-tracking',
  // CUSTOM-END: shipment-tracking

  // Customer can mark the product received (e.g. picked up from provider)
  MARK_RECEIVED_FROM_PURCHASED: 'transition/mark-received-from-purchased',

  // Automatic cancellation happens if none marks the delivery happened
  AUTO_CANCEL: 'transition/auto-cancel',

  // Operator can cancel the purchase before product has been marked as delivered / received
  CANCEL: 'transition/cancel',

  // If provider has marked the product delivered (e.g. shipped),
  // customer can then mark the product received
  MARK_RECEIVED: 'transition/mark-received',

  // If customer doesn't mark the product received manually, it can happen automatically
  AUTO_MARK_RECEIVED: 'transition/auto-mark-received',

  // When provider has marked the product delivered, customer or operator can dispute the transaction
  DISPUTE: 'transition/dispute',
  OPERATOR_DISPUTE: 'transition/operator-dispute',

  // If nothing is done to disputed transaction it ends up to Canceled state
  AUTO_CANCEL_FROM_DISPUTED: 'transition/auto-cancel-from-disputed',

  // Operator can cancel disputed transaction manually
  CANCEL_FROM_DISPUTED: 'transition/cancel-from-disputed',

  // Operator can mark the disputed transaction as received
  MARK_RECEIVED_FROM_DISPUTED: 'transition/mark-received-from-disputed',

  // System moves transaction automatically from received state to complete state
  // This makes it possible to to add notifications to that single transition.
  AUTO_COMPLETE: 'transition/auto-complete',

  // Reviews are given through transaction transitions. Review 1 can be
  // by provider or customer, and review 2 will be the other party of
  // the transaction.
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',

  // CUSTOM-START: cl-neg-01
  // Offer front-phase (CL-NEG-01): buyer/seller can negotiate a price before paying.
  // BUYER_MAKE_OFFER is an entry transition (no `:from` in process.edn, so valid from the
  // initial state), like INQUIRE/REQUEST_PAYMENT above. SELLER_MAKE_OFFER is NOT: since v8
  // (CL-DISC-01 Task 12, live on Dev in v9) it carries `:from :state/inquiry`, because a seller
  // cannot initiate — an initiated transaction's counterparty is the listing's author, who on
  // default-purchase is the seller themselves. That `:from` is D1/Q18's "no cold-sending" made
  // unrepresentable rather than merely un-implemented (offer-initiate.js:73-76).
  BUYER_MAKE_OFFER: 'transition/buyer-make-offer',
  SELLER_MAKE_OFFER: 'transition/seller-make-offer',

  // Counter-offer loop.
  SELLER_COUNTER_OFFER: 'transition/seller-counter-offer',
  BUYER_COUNTER_OFFER: 'transition/buyer-counter-offer',

  // Seller accepts a buyer's offer outright (not privileged: no pricing/metadata action).
  SELLER_ACCEPT_OFFER: 'transition/seller-accept-offer',

  // Declines and withdrawals: both pending states end up in OFFER_DECLINED.
  SELLER_DECLINE_OFFER: 'transition/seller-decline-offer',
  BUYER_WITHDRAW_OFFER: 'transition/buyer-withdraw-offer',
  BUYER_DECLINE_OFFER: 'transition/buyer-decline-offer',
  SELLER_WITHDRAW_OFFER: 'transition/seller-withdraw-offer',

  // 48h timers, anchored to :time/last-entered-state so a counter restarts the clock.
  EXPIRE_BUYER_OFFER: 'transition/expire-buyer-offer',
  EXPIRE_SELLER_OFFER: 'transition/expire-seller-offer',
  EXPIRE_AGREED_OFFER: 'transition/expire-agreed-offer',

  // Payment joins: same actions as REQUEST_PAYMENT, so the stock reservation exists for
  // every downstream stock action.
  REQUEST_PAYMENT_FROM_OFFER: 'transition/request-payment-from-offer',
  REQUEST_PAYMENT_FROM_AGREED: 'transition/request-payment-from-agreed',
  // CUSTOM-END: cl-neg-01
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */

export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PURCHASED: 'purchased',
  DELIVERED: 'delivered',
  RECEIVED: 'received',
  DISPUTED: 'disputed',
  CANCELED: 'canceled',
  COMPLETED: 'completed',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',

  // CUSTOM-START: cl-neg-01
  BUYER_OFFER_PENDING: 'buyer-offer-pending',
  SELLER_OFFER_PENDING: 'seller-offer-pending',
  OFFER_AGREED: 'offer-agreed',
  OFFER_DECLINED: 'offer-declined',
  OFFER_EXPIRED: 'offer-expired',
  // CUSTOM-END: cl-neg-01
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-purchase/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
        // CUSTOM-START: cl-neg-01
        [transitions.BUYER_MAKE_OFFER]: states.BUYER_OFFER_PENDING,
        // CUSTOM-END: cl-neg-01
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
        // CUSTOM-START: cl-neg-01
        // CL-DISC-01 D1: the seller's offer answers an inquiry thread (process.edn's `:from`).
        [transitions.SELLER_MAKE_OFFER]: states.SELLER_OFFER_PENDING,
        // CUSTOM-END: cl-neg-01
      },
    },

    // CUSTOM-START: cl-neg-01
    [states.BUYER_OFFER_PENDING]: {
      on: {
        [transitions.SELLER_ACCEPT_OFFER]: states.OFFER_AGREED,
        [transitions.SELLER_COUNTER_OFFER]: states.SELLER_OFFER_PENDING,
        [transitions.SELLER_DECLINE_OFFER]: states.OFFER_DECLINED,
        [transitions.BUYER_WITHDRAW_OFFER]: states.OFFER_DECLINED,
        [transitions.EXPIRE_BUYER_OFFER]: states.OFFER_EXPIRED,
      },
    },
    [states.SELLER_OFFER_PENDING]: {
      on: {
        [transitions.BUYER_COUNTER_OFFER]: states.BUYER_OFFER_PENDING,
        [transitions.BUYER_DECLINE_OFFER]: states.OFFER_DECLINED,
        [transitions.SELLER_WITHDRAW_OFFER]: states.OFFER_DECLINED,
        [transitions.REQUEST_PAYMENT_FROM_OFFER]: states.PENDING_PAYMENT,
        [transitions.EXPIRE_SELLER_OFFER]: states.OFFER_EXPIRED,
      },
    },
    [states.OFFER_AGREED]: {
      on: {
        [transitions.REQUEST_PAYMENT_FROM_AGREED]: states.PENDING_PAYMENT,
        [transitions.EXPIRE_AGREED_OFFER]: states.OFFER_EXPIRED,
      },
    },
    [states.OFFER_DECLINED]: {},
    [states.OFFER_EXPIRED]: {},
    // CUSTOM-END: cl-neg-01

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PURCHASED,
      },
    },

    [states.PAYMENT_EXPIRED]: {},
    [states.PURCHASED]: {
      on: {
        [transitions.MARK_DELIVERED]: states.DELIVERED,
        [transitions.OPERATOR_MARK_DELIVERED]: states.DELIVERED,
        [transitions.MARK_RECEIVED_FROM_PURCHASED]: states.RECEIVED,
        [transitions.AUTO_CANCEL]: states.CANCELED,
        [transitions.CANCEL]: states.CANCELED,
      },
    },

    [states.CANCELED]: {},

    [states.DELIVERED]: {
      on: {
        // CUSTOM-START: shipment-tracking
        [transitions.UPDATE_TRACKING]: states.DELIVERED,
        // CUSTOM-END: shipment-tracking
        [transitions.MARK_RECEIVED]: states.RECEIVED,
        [transitions.AUTO_MARK_RECEIVED]: states.RECEIVED,
        [transitions.DISPUTE]: states.DISPUTED,
        [transitions.OPERATOR_DISPUTE]: states.DISPUTED,
      },
    },

    [states.DISPUTED]: {
      on: {
        [transitions.AUTO_CANCEL_FROM_DISPUTED]: states.CANCELED,
        [transitions.CANCEL_FROM_DISPUTED]: states.CANCELED,
        [transitions.MARK_RECEIVED_FROM_DISPUTED]: states.RECEIVED,
      },
    },

    [states.RECEIVED]: {
      on: {
        [transitions.AUTO_COMPLETE]: states.COMPLETED,
      },
    },

    [states.COMPLETED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.CONFIRM_PAYMENT,
    transitions.AUTO_CANCEL,
    transitions.CANCEL,
    transitions.MARK_DELIVERED,
    transitions.OPERATOR_MARK_DELIVERED,
    // CUSTOM-START: shipment-tracking
    transitions.UPDATE_TRACKING,
    // CUSTOM-END: shipment-tracking
    transitions.DISPUTE,
    transitions.OPERATOR_DISPUTE,
    transitions.AUTO_COMPLETE,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
    transitions.CANCEL_FROM_DISPUTED,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    // CUSTOM-START: cl-neg-01
    transitions.BUYER_MAKE_OFFER,
    transitions.SELLER_MAKE_OFFER,
    transitions.SELLER_COUNTER_OFFER,
    transitions.BUYER_COUNTER_OFFER,
    transitions.SELLER_ACCEPT_OFFER,
    transitions.SELLER_DECLINE_OFFER,
    transitions.BUYER_WITHDRAW_OFFER,
    transitions.BUYER_DECLINE_OFFER,
    transitions.SELLER_WITHDRAW_OFFER,
    transitions.EXPIRE_BUYER_OFFER,
    transitions.EXPIRE_SELLER_OFFER,
    transitions.EXPIRE_AGREED_OFFER,
    // The two payment joins are DELIBERATELY absent, exactly as REQUEST_PAYMENT and
    // REQUEST_PAYMENT_AFTER_INQUIRY are: they land in `pending-payment`, a state the buyer may
    // never leave, and the feed has nothing true to say about a payment that has not happened.
    // CONFIRM_PAYMENT is already in this list and renders
    // `TransactionPage.ActivityFeed.default-purchase.purchased` ("You placed an order for …"),
    // so the payment story is told once, when it is actually true. Listing the joins here made
    // the feed print the raw id `TransactionPage.ActivityFeed.default-purchase.pending-payment`,
    // a key that does not exist and never did.
    // CUSTOM-END: cl-neg-01
  ].includes(transition);
};
export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [
    transitions.REQUEST_PAYMENT,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY,
    // CUSTOM-START: cl-neg-01
    transitions.BUYER_MAKE_OFFER,
    transitions.SELLER_MAKE_OFFER,
    transitions.SELLER_COUNTER_OFFER,
    transitions.BUYER_COUNTER_OFFER,
    transitions.REQUEST_PAYMENT_FROM_OFFER,
    transitions.REQUEST_PAYMENT_FROM_AGREED,
    // CUSTOM-END: cl-neg-01
  ].includes(transition);
};

// Check when transaction is completed (item is received and review notifications sent)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.AUTO_COMPLETE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (order did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.CANCEL,
    transitions.AUTO_CANCEL,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
    transitions.CANCEL_FROM_DISPUTED,
  ];
  return txRefundedTransitions.includes(transition);
};

export const statesNeedingProviderAttention = [
  states.PURCHASED,
  // CUSTOM-START: cl-neg-01
  states.BUYER_OFFER_PENDING,
  // CUSTOM-END: cl-neg-01
];

// CUSTOM-START: cl-neg-01
export const statesNeedingCustomerAttention = [states.SELLER_OFFER_PENDING, states.OFFER_AGREED];

const OFFER_PRICED_TRANSITIONS_FOR_HISTORY = [
  transitions.BUYER_MAKE_OFFER,
  transitions.SELLER_MAKE_OFFER,
  transitions.SELLER_COUNTER_OFFER,
  transitions.BUYER_COUNTER_OFFER,
];

/**
 * Task 11 §A step 5 — mirrors transactionProcessNegotiation.js's own
 * `getTransitionsWithMatchingOffers` (:383-403), over the four PRICED offer transitions
 * (buyer-make-offer, seller-make-offer, seller-counter-offer, buyer-counter-offer) — the only
 * ones that append an entry to `metadata.offers[]`
 * (`server/api-util/custom/offerTransitions.js`'s `buildOfferMetadataEntry`, called only from
 * offer-initiate.js and offer-transition.js's `runCounter`). The two lists agree by
 * construction (AC-data-2): the server appends exactly one metadata entry per priced transition,
 * in the same call that fires it — so a straight positional zip is safe, with no extra validity
 * gate needed (unlike the negotiation process's own version, which checks
 * `isValidNegotiationOffersArray` first because that process's history can be edited/replaced).
 *
 * `ActivityFeed.js` calls this off the process object itself
 * (`process.getTransitionsWithMatchingOffers`) whenever `metadata.offers` is present, so the feed
 * can quote a figure per round (`negotiationOffer`) without widening itself to know this
 * process's transition names.
 */
export const getTransitionsWithMatchingOffers = (txTransitions, offers) => {
  const withOffers = [];
  let offerIndex = 0;
  for (let i = 0; i < txTransitions.length; i++) {
    const transition = { ...txTransitions[i] };
    if (OFFER_PRICED_TRANSITIONS_FOR_HISTORY.includes(transition.transition)) {
      transition.offerInSubunits = offers[offerIndex]?.offerInSubunits;
      offerIndex++;
    }
    withOffers.push(transition);
  }
  return withOffers;
};
// CUSTOM-END: cl-neg-01
